import { useState } from 'react';
import { ArrowDownCircleIcon, CheckCircle2Icon, FilmIcon, ImageIcon, PlayCircleIcon } from 'lucide-react';
import { app, media, xhs } from '@/lib/bridge';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { displayImageUrl, extractUrl, feedBody, isShortLink, isXhsHost, noteRefFromUrl, parseFeed, type FeedNote } from '@/lib/xhsLinks';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { CARD, HINT } from '@/components/ui';
import { ErrorCard, PasteButton, readClipboardText, ToolNotices, useXhsTool, xhsErrorText } from './common';

/**
 * 薯薯 · 小红书无水印下载（`/shushu/xhs/download?accountId=`）。
 *
 * 移植自搬小书 `mobile/tools/download` 的小红书本地分支：用当前账号调 feed 拿原图 / 视频 / 实况图视频分量，
 * 再 `media.save` 存相册（原生限定平台 CDN、http 自动升 https）。不接搬小书服务端的第三方解析，也不扣额度；
 * 只支持小红书链接。
 */

type SaveItem = { url: string; type: 'image' | 'video' };

function saveItems(note: FeedNote): SaveItem[] {
  if (note.videoUrl) return [{ url: note.videoUrl, type: 'video' }];
  return note.images.flatMap((img) => [
    { url: img.url, type: 'image' as const },
    ...(img.liveVideoUrl ? [{ url: img.liveVideoUrl, type: 'video' as const }] : []),
  ]);
}

export function ShushuXhsDownloadPage() {
  const tool = useXhsTool();
  const { account, usable, loaded } = tool;
  const [busy, setBusy] = useState<'parse' | 'save' | null>(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState<FeedNote | null>(null);
  /** 保存结果：null = 还没保存 */
  const [saved, setSaved] = useState<{ saved: number; failed: number } | null>(null);
  useGroupedSurface();
  useAppReady(loaded);

  const parse = async (raw: string) => {
    if (!account) return;
    const url = extractUrl(raw) ?? raw.trim();
    if (!url || !isXhsHost(url)) {
      setError('只支持小红书笔记链接，请在小红书里点「分享 → 复制链接」');
      return;
    }
    setBusy('parse');
    setError('');
    setNote(null);
    setSaved(null);
    try {
      const full = isShortLink(url) ? (await app.resolveUrl(url)).url : url;
      const ref = noteRefFromUrl(full);
      if (!ref) {
        setError('这不是笔记链接，请复制笔记的分享链接');
        return;
      }
      const { data } = await xhs.request(account.id, { method: 'POST', path: '/api/sns/web/v1/feed', body: feedBody(ref) });
      const parsed = parseFeed(data, ref.noteId);
      if (!parsed || saveItems(parsed).length === 0) {
        setError('没能解析出图片或视频，笔记可能已删除或仅作者可见');
        return;
      }
      setNote(parsed);
      void app.haptic('success');
    } catch (e) {
      setError(xhsErrorText(e));
    } finally {
      setBusy(null);
    }
  };

  const paste = async () => {
    try {
      await parse(await readClipboardText('剪贴板是空的，请先在小红书里复制笔记链接'));
    } catch (e) {
      setError(xhsErrorText(e));
    }
  };

  const save = async () => {
    if (!note) return;
    setBusy('save');
    setError('');
    try {
      const result = await media.save(saveItems(note));
      setSaved(result);
      void app.haptic(result.failed ? 'error' : 'success');
    } catch (e) {
      setError(xhsErrorText(e));
    } finally {
      setBusy(null);
    }
  };

  const items = note ? saveItems(note) : [];
  const cover = displayImageUrl(note?.coverUrl);
  const lives = note?.images.filter((i) => i.liveVideoUrl).length ?? 0;
  const summary = note
    ? note.videoUrl
      ? '视频'
      : `${note.images.length} 张图片${lives ? `（含 ${lives} 张实况，视频单独保存）` : ''}`
    : '';
  const allSaved = !!saved && saved.failed === 0;

  return (
    <PageShell title="无水印下载" description="粘贴小红书笔记链接，保存原图 / 视频到相册">
      {!loaded ? <PageSkeleton kind="linkTool" /> : null}
      <ToolNotices {...tool} />

      {loaded && usable ? (
        <div className="flex flex-col items-center gap-3">
          <PasteButton
            title="粘贴链接并解析"
            subtitle="自动识别剪贴板内容"
            busyText="正在解析笔记信息…"
            busy={busy === 'parse'}
            disabled={busy === 'save'}
            onClick={() => void paste()}
          />
          <p className={HINT}>支持小红书图文、视频、实况图</p>
        </div>
      ) : null}

      {note ? (
        <Card className={CARD}>
          <div className="flex items-start gap-3">
            <div className="bg-muted text-muted-foreground relative flex h-24 w-18 shrink-0 items-center justify-center overflow-hidden rounded-icon">
              {cover ? (
                <img className="size-full object-cover" src={cover} alt="" referrerPolicy="no-referrer" />
              ) : note.videoUrl ? (
                <FilmIcon className="size-5.5" />
              ) : (
                <ImageIcon className="size-5.5" />
              )}
              {note.videoUrl ? (
                <span className="absolute inset-0 flex items-center justify-center text-white/90 drop-shadow">
                  <PlayCircleIcon className="size-7" />
                </span>
              ) : null}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 text-subheadline font-medium">
                <CheckCircle2Icon className="size-3.75 shrink-0 text-green-600" />
                解析成功
              </div>
              {note.title.trim() ? <p className="mt-1 line-clamp-2 text-footnote leading-snug">{note.title}</p> : null}
              <p className={`mt-1 ${HINT}`}>{summary}</p>
            </div>
          </div>
          {!note.videoUrl && note.images.length > 1 ? (
            <div className="mt-1 grid grid-cols-4 gap-1.5">
              {note.images.slice(0, 8).map((img, i) => (
                <div key={`${img.url}-${i}`} className="bg-muted relative aspect-[3/4] overflow-hidden rounded-field">
                  <img className="size-full object-cover" src={displayImageUrl(img.url) ?? ''} alt="" loading="lazy" referrerPolicy="no-referrer" />
                  {img.liveVideoUrl ? (
                    <span className="absolute top-1 left-1 rounded-field bg-black/55 px-1.5 py-px text-caption2 text-white">实况</span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
          <div className="bg-border my-1 h-px" />
          <Button className="w-full" disabled={!!busy || allSaved} onClick={() => void save()}>
            {allSaved ? (
              <>
                <CheckCircle2Icon data-icon="inline-start" />
                已保存到相册
              </>
            ) : busy === 'save' ? (
              <>
                <Spinner />
                下载中…
              </>
            ) : (
              <>
                <ArrowDownCircleIcon data-icon="inline-start" />
                {note.videoUrl ? '下载无水印视频' : `下载全部 ${items.length} 个文件`}
              </>
            )}
          </Button>
          <p className={HINT}>请只保存你有权使用的内容；转载他人作品请先取得作者同意。</p>
          {saved && saved.failed ? (
            <p className={`${HINT} text-destructive`}>
              已保存 {saved.saved} 个，{saved.failed} 个失败，可以再点一次重试。
            </p>
          ) : null}
        </Card>
      ) : null}

      {error ? <ErrorCard title="解析失败" message={error} /> : null}

    </PageShell>
  );
}
